import { useCallback, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "../assets/user_context";
import {
  levelForSubscription,
  minLevelFor,
  resolveEntitlements,
  resolveLimits,
} from "../config/entitlements";

/**
 * Effective entitlements for the current org: plan entitlements (derived
 * from the subscription doc) merged with admin featureOverrides.
 *
 * This is the single frontend gating source (plan §3.4) — new code should
 * use this instead of `features.*` from context, `subscriptionLevel ===
 * "plus"` comparisons, or the binary has-any-subscription check.
 */
export const useEntitlements = () => {
  const { subscriptionData, orgData } = useContext(UserContext);
  const featureOverrides = orgData?.featureOverrides;
  return useMemo(
    () =>
      resolveEntitlements(
        levelForSubscription(subscriptionData),
        featureOverrides,
      ),
    [subscriptionData, featureOverrides],
  );
};

/** Convenience: `useEntitlement("payroll")` → boolean */
export const useEntitlement = (key) => useEntitlements()[key] === true;

/**
 * Numeric plan limits ({ activeIntegrations, nonAuditorUsers }; null =
 * unlimited). Integration tiles gate on `activeIntegrations !== 0` — there
 * is no boolean "integrations" entitlement, the cap IS the gate.
 */
export const usePlanLimits = () => {
  const { subscriptionData } = useContext(UserContext);
  return useMemo(
    () => resolveLimits(levelForSubscription(subscriptionData)),
    [subscriptionData],
  );
};

/**
 * Integration tiles/connect surfaces gate: a plan with a zero integration
 * cap is locked; null cap = unlimited (truthy ≠ 0, so the predicate holds).
 */
export const useIntegrationAccess = () =>
  usePlanLimits().activeIntegrations !== 0;

/**
 * Single seam for every locked-surface upgrade CTA. Returns
 * `triggerUpgrade(requires, featureLabel)`:
 *  - subscribed orgs deep-link into billing with the unlocking tier
 *    pre-recommended (?recommend=&feature= — consumed by BillingPage),
 *  - unsubscribed orgs get the subscribe modal seeded the same way.
 * Returns false (and does nothing) when `requires` is absent/unknown so
 * call sites can keep their legacy no-entitlement-key behavior.
 */
export const useUpgradeCta = () => {
  const { subscriptionData, setOpenSubscribe } = useContext(UserContext);
  const navigate = useNavigate();
  return useCallback(
    (requires, featureLabel) => {
      const recommend = requires ? minLevelFor(requires) : null;
      if (!recommend) return false;
      if (
        subscriptionData?.subscriptionCurrent ||
        subscriptionData?.freeTrial
      ) {
        navigate(
          `/account/billing?recommend=${recommend}&feature=${encodeURIComponent(featureLabel || "")}`,
        );
      } else {
        setOpenSubscribe({
          recommend,
          featureLabel: featureLabel || undefined,
        });
      }
      return true;
    },
    [subscriptionData, setOpenSubscribe, navigate],
  );
};
